import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import type { GolfActivityDetailDto, GolfActivityDto, GolfConfig, GolfShotDto, GolfShotsDto } from '@jordan/types';
import { Alert, Breadcrumb, EChart, PageHeader, SelectMenu, Spinner } from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { apiGet } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';
import { clubColor, orderClubs } from './golfClubs';
import { GOLF_METRICS, formatMetric, type GolfMetric } from './golfMetrics';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const formatDay = (s: string) =>
  new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const DATE_PRESETS = [
  { value: 'last30', label: 'Last 30 days', days: 30 },
  { value: 'last7', label: 'Last 7 days', days: 7 },
  { value: 'last90', label: 'Last 90 days', days: 90 },
  { value: 'last365', label: 'Last 12 months', days: 365 },
  { value: 'all', label: 'All time', days: null },
] as const;

function dateRange(preset: string): { from?: string; to?: string } {
  const days = DATE_PRESETS.find((p) => p.value === preset)?.days ?? null;
  if (!days) return {};
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: ymd(from), to: ymd(new Date()) };
}

interface ScatterBands {
  fairway: number;
  rough: number;
  minAxis: number;
}

// --- Scatter: Y = carry distance, X = carry deviation (signed; − = left, + = right) ---
function scatterOption(shots: GolfShotDto[], visibleClubs: string[], bands: ScatterBands): EChartsOption {
  const usable = shots.filter((s) => s.carryDistance != null && s.carryDeviationDistance != null);
  const maxAbs = Math.max(bands.rough, ...usable.map((s) => Math.abs(s.carryDeviationDistance as number)));
  // Axis is fixed at ±minAxis yds, expanding (in 40-yd steps) when shots are wider.
  const bound = Math.max(bands.minAxis, Math.ceil(maxAbs / 40) * 40);
  // Vertical zones: green fairway (±fairway), amber rough (out to ±rough), red out-of-bounds beyond.
  const zones = [
    [{ xAxis: -bands.fairway, itemStyle: { color: 'rgba(34,197,94,0.16)' } }, { xAxis: bands.fairway }],
    [{ xAxis: -bands.rough, itemStyle: { color: 'rgba(234,179,8,0.13)' } }, { xAxis: -bands.fairway }],
    [{ xAxis: bands.fairway, itemStyle: { color: 'rgba(234,179,8,0.13)' } }, { xAxis: bands.rough }],
    [{ xAxis: -bound, itemStyle: { color: 'rgba(239,68,68,0.09)' } }, { xAxis: -bands.rough }],
    [{ xAxis: bands.rough, itemStyle: { color: 'rgba(239,68,68,0.09)' } }, { xAxis: bound }],
  ];
  return {
    textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    legend: { show: false },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'var(--white)',
      borderColor: 'var(--gray-400)',
      className: 'shadow-sm rounded',
      textStyle: { color: 'var(--body-color)' },
      formatter: (p: any) => {
        const x = p.value[0] as number;
        const side = x < 0 ? ' L' : x > 0 ? ' R' : '';
        return `<div class="fw-semibold mb-1">${p.seriesName}</div>Carry: <b>${Math.round(p.value[1])} yds</b><br/>Dispersion: <b>${Math.round(Math.abs(x))} yds${side}</b>`;
      },
    },
    xAxis: {
      type: 'value',
      min: -bound,
      max: bound,
      interval: 40,
      name: 'Dispersion (yds)',
      nameLocation: 'middle',
      nameGap: 30,
      axisLabel: { color: 'rgba(var(--body-color-rgb), .65)', formatter: (v: number) => String(Math.abs(v)) },
      splitLine: { lineStyle: { color: 'var(--gray-200)' } },
    },
    yAxis: {
      type: 'value',
      min: 0,
      name: 'Carry (yds)',
      axisLabel: { color: 'rgba(var(--body-color-rgb), .65)' },
      splitLine: { lineStyle: { color: 'var(--gray-200)' } },
    },
    series: [
      // Background fairway/rough/OB bands (drawn first, behind the points).
      { type: 'line', data: [], silent: true, animation: false, markArea: { silent: true, data: zones } },
      ...visibleClubs.map((club) => ({
        name: club,
        type: 'scatter',
        symbolSize: 11,
        itemStyle: { color: clubColor(club), opacity: 0.85 },
        data: usable.filter((s) => s.club === club).map((s) => [s.carryDeviationDistance, s.carryDistance]),
      })),
    ],
  } as EChartsOption;
}

interface ClubStat {
  club: string;
  count: number;
  min: number;
  max: number;
  avg: number;
}

function clubStats(shots: GolfShotDto[], orderedClubs: string[], metric: GolfMetric): ClubStat[] {
  const out: ClubStat[] = [];
  for (const club of orderedClubs) {
    const vals = shots
      .filter((s) => s.club === club)
      .map((s) => s[metric.field])
      .filter((v): v is number => v != null);
    if (!vals.length) continue;
    const sum = vals.reduce((a, b) => a + b, 0);
    out.push({ club, count: vals.length, min: Math.min(...vals), max: Math.max(...vals), avg: sum / vals.length });
  }
  return out;
}

export function GolfActivitySummaryPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id = 'all' } = useParams<{ id: string }>();
  const isAll = id === 'all';

  const [activities, setActivities] = useState<GolfActivityDto[]>([]);
  const [shots, setShots] = useState<GolfShotDto[] | null>(null);
  const [activity, setActivity] = useState<GolfActivityDto | null>(null);
  const [activityCount, setActivityCount] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('last30');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // Default the distances card to Avg Total Distance (fall back to the first metric if renamed).
  const [metricKey, setMetricKey] = useState<string>(
    GOLF_METRICS.find((m) => m.key === 'totalDistance')?.key ?? GOLF_METRICS[0].key,
  );
  // Fairway/rough/axis come from config; fall back to sensible defaults until loaded.
  const [bands, setBands] = useState<ScatterBands>({ fairway: 20, rough: 40, minAxis: 80 });

  // Activity list (drives the event picker + the type-filter options).
  useEffect(() => {
    apiGet<GolfActivityDto[]>('/api/health/golf/activities').then(setActivities).catch(() => undefined);
    apiGet<GolfConfig>('/api/config/golf')
      .then((c) =>
        setBands({ fairway: c.fairwayHalfWidthYds, rough: c.roughHalfWidthYds, minAxis: c.scatterMinAxisYds }),
      )
      .catch(() => undefined);
  }, []);

  // Load shots: All Data (filtered) or one activity.
  useEffect(() => {
    setShots(null);
    setErr(null);
    if (isAll) {
      setActivity(null);
      const { from, to } = dateRange(datePreset);
      const qs = new URLSearchParams();
      if (typeFilter !== 'all') qs.set('type', typeFilter);
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      apiGet<GolfShotsDto>(`/api/health/golf/shots?${qs.toString()}`)
        .then((r) => {
          setShots(r.shots);
          setActivityCount(r.activityCount);
        })
        .catch(() => setErr('Could not load shots.'));
    } else {
      apiGet<GolfActivityDetailDto>(`/api/health/golf/activities/${id}`)
        .then((r) => {
          setShots(r.shots);
          setActivity(r.activity);
        })
        .catch(() => setErr('Could not load this activity.'));
    }
  }, [id, isAll, typeFilter, datePreset]);

  const typeOptions = useMemo(() => {
    const types = [...new Set(activities.map((a) => a.type))].sort();
    return [{ value: 'all', label: 'All Data' }, ...types.map((t) => ({ value: t, label: t }))];
  }, [activities]);

  // Event picker: All Data + the activities matching the current filters (the selected one is
  // always included so a deep-linked event still shows even if outside the filter window).
  const eventOptions = useMemo(() => {
    const { from, to } = dateRange(datePreset);
    const inFilter = activities.filter(
      (a) =>
        (typeFilter === 'all' || a.type === typeFilter) &&
        (!from || a.date >= from) &&
        (!to || a.date <= to),
    );
    const ids = new Set(inFilter.map((a) => a.id));
    const extra = !isAll && !ids.has(id) ? activities.filter((a) => a.id === id) : [];
    return [
      { value: 'all', label: 'All Data' },
      ...[...inFilter, ...extra].map((a) => ({ value: a.id, label: `${formatDay(a.date)} · ${a.type}` })),
    ];
  }, [activities, typeFilter, datePreset, id, isAll]);

  const clubs = useMemo(
    () => orderClubs([...new Set((shots ?? []).map((s) => s.club).filter((c): c is string => !!c))]),
    [shots],
  );
  const visibleClubs = useMemo(() => clubs.filter((c) => !hidden.has(c)), [clubs, hidden]);
  const metric = GOLF_METRICS.find((m) => m.key === metricKey) ?? GOLF_METRICS[0];
  const stats = useMemo(() => (shots ? clubStats(shots, clubs, metric) : []), [shots, clubs, metric]);
  const globalMax = useMemo(() => Math.max(1, ...stats.map((s) => s.max)), [stats]);
  const scatterOpt = useMemo(() => scatterOption(shots ?? [], visibleClubs, bands), [shots, visibleClubs, bands]);

  const toggleClub = (club: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(club)) next.delete(club);
      else next.add(club);
      return next;
    });

  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / globalMax) * 100))}%`;
  const dateLabel = DATE_PRESETS.find((p) => p.value === datePreset)?.label ?? '';

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">{isAll ? 'All data' : activity ? activity.type : 'Activity'}</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Golf' },
              { label: 'Activity', href: '/health/golf/activity' },
              { label: 'Summary' },
            ]}
          />
        }
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {/* Event picker + filters */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div style={{ minWidth: 260 }}>
            <SelectMenu
              className="mb-0"
              icon="ph-flag-checkered"
              value={id}
              onChange={(v) => navigate(`/health/golf/activity/${v}`)}
              options={eventOptions}
            />
          </div>
          <div className="ms-auto d-flex flex-wrap gap-2">
            <div style={{ minWidth: 170 }}>
              <SelectMenu className="mb-0" icon="ph-funnel" value={typeFilter} onChange={setTypeFilter} options={typeOptions} />
            </div>
            <div style={{ minWidth: 170 }}>
              <SelectMenu
                className="mb-0"
                icon="ph-calendar-blank"
                value={datePreset}
                onChange={setDatePreset}
                options={DATE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
              />
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center mb-3 text-muted">
          <i className="ph-golf me-2" />
          {isAll ? (
            <span>
              <span className="fw-semibold">All data</span> · {activityCount} activities ·{' '}
              {shots?.length ?? 0} shots · {dateLabel}
            </span>
          ) : activity ? (
            <span>
              <span className="fw-semibold">{formatDay(activity.date)}</span> · {activity.shotCount} shots ·{' '}
              {activity.clubCount} clubs
            </span>
          ) : null}
        </div>

        {!shots ? (
          <Spinner />
        ) : shots.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center text-muted py-5">No shots for this selection.</div>
          </div>
        ) : (
          <>
            {/* Card 1: shot dispersion scatter */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-transparent">
                <h5 className="mb-0">Shot dispersion</h5>
                <span className="text-muted fs-sm">
                  <span style={{ color: '#16a34a' }}>Fairway</span> ±{bands.fairway} yds ·{' '}
                  <span style={{ color: '#ca8a04' }}>Rough</span> ±{bands.rough} yds ·{' '}
                  <span style={{ color: '#dc2626' }}>Out of bounds</span> beyond
                </span>
              </div>
              <div className="card-body">
                {visibleClubs.length === 0 ? (
                  <div className="text-center text-muted py-5">All clubs hidden — select one below.</div>
                ) : (
                  <EChart option={scatterOpt} height={420} />
                )}
                <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
                  {clubs.map((club) => {
                    const off = hidden.has(club);
                    return (
                      <button
                        key={club}
                        type="button"
                        className="btn btn-link p-0 d-inline-flex align-items-center text-decoration-none"
                        style={{ opacity: off ? 0.4 : 1 }}
                        onClick={() => toggleClub(club)}
                      >
                        <span
                          className="d-inline-block rounded-circle me-2"
                          style={{ width: 12, height: 12, background: clubColor(club) }}
                        />
                        <span className={`small ${off ? 'text-muted text-decoration-line-through' : 'text-body'}`}>
                          {club}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Card 2: distances per club */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent d-flex align-items-center flex-wrap gap-2">
                <h5 className="mb-0 me-auto">Distances by club</h5>
                <div style={{ minWidth: 220 }}>
                  <SelectMenu
                    className="mb-0"
                    icon="ph-ruler"
                    value={metricKey}
                    onChange={setMetricKey}
                    options={GOLF_METRICS.map((m) => ({ value: m.key, label: m.label }))}
                  />
                </div>
              </div>
              <div className="card-body">
                {stats.length === 0 ? (
                  <div className="text-center text-muted py-4">No data for this metric.</div>
                ) : (
                  stats.map((s) => {
                    const color = clubColor(s.club);
                    return (
                      <div key={s.club} className="py-3 border-bottom">
                        <div className="d-flex align-items-baseline mb-2">
                          <span className="fw-semibold">{s.club}</span>
                          <span className="ms-auto fw-bold fs-5">
                            {formatMetric(metric, s.avg)} <span className="text-muted fs-sm fw-normal">{metric.unit}</span>
                          </span>
                        </div>
                        <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'var(--gray-200)' }}>
                          <div
                            style={{
                              position: 'absolute',
                              left: pct(s.min),
                              width: `${Math.max(0, Math.min(100, ((s.max - s.min) / globalMax) * 100))}%`,
                              height: '100%',
                              borderRadius: 4,
                              background: color,
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              left: pct(s.avg),
                              top: '50%',
                              width: 14,
                              height: 14,
                              marginLeft: -7,
                              marginTop: -7,
                              borderRadius: '50%',
                              background: 'var(--white)',
                              border: `3px solid ${color}`,
                            }}
                          />
                        </div>
                        <div className="d-flex justify-content-between text-muted fs-sm mt-1">
                          <span>0</span>
                          <span>{s.count} shots</span>
                          <span>{formatMetric(metric, globalMax)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AppChrome>
  );
}
